import React from "react";
import { Trans, t } from "@lingui/macro";
import { Box } from "@mui/material";
import EndScreen from "@root/components/molecules/EndScreen/Component";
import Introduction from "@root/components/molecules/Introduction/Component";
import { VdaView } from "@root/components/organisms/VdaView/Component";

import { useAppContext } from "@root/global/context";
import { ProgressStateVda } from "@root/misc/constants";
import { checkTitle } from "@root/misc/helpers";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

/** Returns the duration of the test in minutes given a range. */
function duration(range: number): number {
  if (range === 1) {
    return 10;
  } else if (range === 2) {
    return 20;
  } else return 30;
}

// For each different step in a test, this file renders the HTML for that specific step.
export function Vda(): JSX.Element {
  const { progressStateVda, setProgressStateVda, vdaData } = useAppContext();
  const location = useLocation();
  const { variant } = useParams();
  const [searchParams] = useSearchParams();
  const range: number = parseInt(searchParams.get("range") ?? "3");

  if (progressStateVda === ProgressStateVda.Introduction) {
    return (
      <Introduction
        key="vda1"
        part={0}
        nextFn={() => setProgressStateVda(ProgressStateVda.InstructionPart1)}
        title={checkTitle(location.pathname)}
        nextButtonLabel={t({
          id: "vda.introduction.title",
          message: "Instructies lezen",
        })}
        description={
          <p>
            <Trans id="vda.introduction">
              Bij deze test gaat het om uw langdurige aandacht. In de test moet u soms reageren op
              patronen met lichtsignalen.
              <br />
              <br />
              Er zijn twee soorten signalen: series en velden.
              <br />
              <br />
              Een veld is een groep signalen die tegelijk worden getoond.
              <br />
              Een serie is een reeks van 3 signalen die achter elkaar worden getoond.
            </Trans>
          </p>
        }
      />
    );
  }

  if (progressStateVda === ProgressStateVda.InstructionPart1) {
    return (
      <Introduction
        key="vda21"
        nextFn={() => setProgressStateVda(ProgressStateVda.Practise1)}
        title={checkTitle(location.pathname)}
        nextButtonLabel={t({
          id: "vda.instruction.title.part1",
          message: "Oefenen met velden",
        })}
        description={
          <p>
            {variant === "on-location" && (
              <Trans id="vda.instruction.part1.on-location">
                Een veld is een groep lampjes die tegelijk worden getoond. Het gaat om een aantal
                lampjes met verschillende kleuren. Deze groep lampjes worden gedurende 2 seconden
                getoond. U moet reageren als er 5 lampjes zijn die dezelfde kleur hebben.
                <br />
                <br />
                Als er 5 lampjes dezelfde kleur hebben drukt u op de “L” toets van het toetsenbord.
                <br />
                Als er geen 5 lampjes zijn met dezelfde kleur hoeft u niets te doen.
                <br />
                <br />U krijgt nu een aantal velden om te oefenen.
              </Trans>
            )}

            {variant === "home" && (
              <Trans id="vda.instruction.part1.home">
                Een veld is een groep lampjes die tegelijk worden getoond. Het gaat om een aantal
                lampjes met verschillende kleuren. Deze groep lampjes worden gedurende 2 seconden
                getoond. U moet reageren als er 5 lampjes zijn die dezelfde kleur hebben.
                <br />
                <br />
                Als er 5 lampjes dezelfde kleur hebben drukt u op de linker Shift toets van het
                toetsenbord.
                <br />
                Als er geen 5 lampjes zijn met dezelfde kleur hoeft u niets te doen.
                <br />
                <br />U krijgt nu een aantal velden om te oefenen.
              </Trans>
            )}
          </p>
        }
      />
    );
  }

  if (progressStateVda === ProgressStateVda.Practise1) {
    return (
      <VdaView
        part={1}
        key="practise-test1"
        isPractise
        endFn={() => setProgressStateVda(ProgressStateVda.InstructionPart2)}
        variant={variant ?? "on-location"}
        range={range}
      />
    );
  }

  if (progressStateVda === ProgressStateVda.InstructionPart2) {
    return (
      <Introduction
        key="vda22"
        nextFn={() => setProgressStateVda(ProgressStateVda.Practise2)}
        title={checkTitle(location.pathname)}
        nextButtonLabel={t({
          id: "vda.instruction.title.part2",
          message: "Oefenen met series",
        })}
        description={
          <p>
            {variant === "on-location" && (
              <Trans id="vda.instruction.part2.on-location">
                Een serie is een groep van drie lampjes die achter elkaar worden getoond. Elk lampje
                verschijnt een halve seconde, daarna komt de volgende. De lampjes hebben
                verschillende kleuren. U moet alleen reageren als de drie lampjes in de volgorde
                groen-geel-rood worden getoond.
                <br />
                <br />
                Als de 3 lampjes in de volgorde groen-geel-rood verschijnen drukt u op de “H” toets
                van het toetsenbord.
                <br />
                Als de lampjes in een andere volgorde verschijnen hoeft u niets te doen.
                <br />
                <br />U krijgt nu een aantal series om te oefenen.
              </Trans>
            )}
            {variant === "home" && (
              <Trans id="vda.instruction.part2.home">
                Een serie is een groep van drie lampjes die achter elkaar worden getoond. Elk lampje
                verschijnt een halve seconde, daarna komt de volgende. De lampjes hebben
                verschillende kleuren. U moet alleen reageren als de drie lampjes in de volgorde
                groen-geel-rood worden getoond.
                <br />
                <br />
                Als de 3 lampjes in de volgorde groen-geel-rood verschijnen drukt u op de rechter
                Shift toets van het toetsenbord.
                <br />
                Als de lampjes in een andere volgorde verschijnen hoeft u niets te doen.
                <br />
                <br />U krijgt nu een aantal series om te oefenen.
              </Trans>
            )}
          </p>
        }
      />
    );
  }
  if (progressStateVda === ProgressStateVda.Practise2) {
    return (
      <VdaView
        part={2}
        key="practise-test2"
        isPractise
        endFn={() => setProgressStateVda(ProgressStateVda.InstructionPart3)}
        variant={variant ?? "on-location"}
        range={range}
      />
    );
  }

  if (progressStateVda === ProgressStateVda.InstructionPart3) {
    return (
      <Introduction
        key="vda22"
        nextFn={() => setProgressStateVda(ProgressStateVda.Practise3)}
        title={checkTitle(location.pathname)}
        nextButtonLabel={t({
          id: "vda.instruction.title.part3",
          message: "Oefenen met velden en series",
        })}
        description={
          <p>
            {variant === "on-location" && (
              <Trans id="vda.instruction.part3.on-location">
                In de test worden velden en series tegelijk getoond. Eerst verschijnt een veld, en
                een halve seconde later begint de serie.
                <br />
                <br />
                Als een veld wordt getoond met 5 lampjes met dezelfde kleur drukt u op de “L” toets.
                <br />
                Als de serie verschijnt in de volgorde groen-geel-rood drukt u op de “H” toets.
                <br />
                Als een veld met 5 dezelfde lampjes verschijnt, en ook een serie groen-geel-rood,
                dan drukt u op de “L” en de “H” toets.
                <br />
                <br />
                De signalen van de serie tellen <strong>niet</strong> mee voor het aantal lampjes in
                het veld.
                <br />
                <br />U krijgt nu een oefening met velden en series. Dit is net als bij de echte
                test, alleen gaat het in de echte test sneller.
              </Trans>
            )}
            {variant === "home" && (
              <Trans id="vda.instruction.part3.home">
                In de test worden velden en series tegelijk getoond. Eerst verschijnt een veld, en
                een halve seconde later begint de serie.
                <br />
                <br />
                Als een veld wordt getoond met 5 lampjes met dezelfde kleur drukt u op de linker
                Shift toets.
                <br />
                Als de serie verschijnt in de volgorde groen-geel-rood drukt u op de rechter Shift
                toets.
                <br />
                Als een veld met 5 dezelfde lampjes verschijnt, en ook een serie groen-geel-rood,
                dan drukt u op de linker Shift toets en de rechter Shift toets.
                <br />
                <br />
                De signalen van de serie tellen <strong>niet</strong> mee voor het aantal lampjes in
                het veld.
                <br />
                <br />U krijgt nu een oefening met velden en series. Dit is net als bij de echte
                test, alleen gaat het in de echte test sneller.
              </Trans>
            )}
          </p>
        }
      />
    );
  }

  if (progressStateVda === ProgressStateVda.Practise3) {
    return (
      <VdaView
        part={3}
        key="practise-test3"
        isPractise
        endFn={() => setProgressStateVda(ProgressStateVda.InstructionPart4)}
        variant={variant ?? "on-location"}
        range={range}
      />
    );
  }

  if (progressStateVda === ProgressStateVda.InstructionPart4) {
    return (
      <Introduction
        key="vda22"
        nextFn={() => setProgressStateVda(ProgressStateVda.Test)}
        title={checkTitle(location.pathname)}
        nextButtonLabel={t({
          id: "vda.instruction.title.part4",
          message: "Start de test",
        })}
        description={
          <>
            <p>
              <Trans id="vda.instruction.part4.summary">Nog een keer een samenvatting:</Trans>
            </p>
            <Box
              component="table"
              sx={{
                borderCollapse: "collapse",
                "& th, & td": {
                  textAlign: "left",
                  py: 1,
                  pr: 4,
                  borderBottom: 1,
                  borderColor: "grey.400",
                },
                "& th": { fontWeight: "normal" },
                "& td:first-of-type": { fontWeight: "bold" },
              }}
            >
              <thead>
                <tr>
                  <th>
                    <Trans id="vda.instruction.part4.table.image">Beeld</Trans>
                  </th>
                  <th>
                    <Trans id="vda.instruction.part4.table.reaction">Reactie</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Trans id="vda.instruction.part4.table.fields">
                      Velden: vijf lampen in dezelfde kleur
                    </Trans>
                  </td>
                  <td>
                    {variant === "home" ? (
                      <Trans id="vda.instruction.part4.table.fields-reaction.home">
                        <strong>Linker</strong> Shift-toets
                      </Trans>
                    ) : (
                      <Trans id="vda.instruction.part4.table.fields-reaction.on-location">
                        <strong>“L”</strong>-toets
                      </Trans>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <Trans id="vda.instruction.part4.table.series">
                      Serie: kleurenvolgorde groen‑geel‑rood
                    </Trans>
                  </td>
                  <td>
                    {variant === "home" ? (
                      <Trans id="vda.instruction.part4.table.series-reaction.home">
                        <strong>Rechter</strong> Shift-toets
                      </Trans>
                    ) : (
                      <Trans id="vda.instruction.part4.table.series-reaction.on-location">
                        <strong>“H”</strong>-toets
                      </Trans>
                    )}
                  </td>
                </tr>
              </tbody>
            </Box>
            <p>
              <Trans id="vda.instruction.part4.outro">
                In de echte test verschijnen de series en velden veel sneller dan tijdens de
                oefening.
                <br />
                Als het een keer niet lukt om op beide signalen tegelijk te letten, geef dan
                voorrang aan de series groen‑geel‑rood.
                <br />
                <br />U kunt nu met de echte test beginnen. Deze test duurt {duration(range)}{" "}
                minuten.
              </Trans>
            </p>
          </>
        }
      />
    );
  }

  if (progressStateVda === ProgressStateVda.End) {
    return <EndScreen type="vda" variant={variant ?? "on-location"} data={vdaData} />;
  }

  return (
    <VdaView
      key="test"
      endFn={() => setProgressStateVda(ProgressStateVda.End)}
      variant={variant ?? "on-location"}
      range={range}
    />
  );
}
